import React, { useEffect } from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  IconButton,
  MenuItem,
  Modal,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "@hooks";
import { RootState } from "@Store";
import { matrixServersListRequest } from "@Store/Matrix/actions";
import { selectMatrixServers } from "@Store/Matrix/selectors";
import { appsListRequest } from "@Store/Apps/actions";
import { selectAppsList } from "@Store/Apps/selectors";
import {
  createOrganizationRequest,
  resetCreateOrganizationModal,
} from "@Store/Organizations/actions";
import {
  selectCreateOrganizationModal,
  selectShowCreateOrganizationModal,
} from "@Store/Organizations/selectors";
import { IMatrixServer } from "@API/Matrix/types";
import { IApp } from "@API/Apps/types";

const CreateOrganizationModal: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const { matrixServers, apps, modal, modalIsVisible } = useAppSelector(
    (state: RootState) => ({
      matrixServers: selectMatrixServers(state),
      apps: selectAppsList(state),
      modal: selectCreateOrganizationModal(state),
      modalIsVisible: selectShowCreateOrganizationModal(state),
    })
  );

  const uniqueMatrixServers = matrixServers.filter(
    (server: IMatrixServer, index: number, list: IMatrixServer[]) =>
      list.findIndex((item) => item.identifier === server.identifier) === index
  );

  useEffect(() => {
    if (modalIsVisible) {
      dispatch(matrixServersListRequest());
      dispatch(appsListRequest());
    }
  }, [dispatch, modalIsVisible]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      orgName: "",
      appId: "",
      matrixServerIdentifier: "",
      adminBpUserName: "",
      addAdmin: false,
    },
    validationSchema: Yup.object({
      orgName: Yup.string().required(t("errors.validation.requiredField")),
      appId: Yup.string().required(t("errors.validation.requiredField")),
      matrixServerIdentifier: Yup.string().required(
        t("errors.validation.requiredField")
      ),
      addAdmin: Yup.boolean(),
      adminBpUserName: Yup.string().when("addAdmin", {
        is: true,
        then: Yup.string()
          .email(t("errors.validation.emailFormat"))
          .required(t("errors.validation.requiredField")),
        otherwise: Yup.string().nullable(),
      }),
    }),
    onSubmit: (values) => {
      dispatch(
        createOrganizationRequest({
          orgName: values.orgName.trim(),
          appId: values.appId,
          matrixServerIdentifier: values.matrixServerIdentifier,
          adminBpUserName:
            values.addAdmin && values.adminBpUserName
              ? values.adminBpUserName.trim()
              : null,
        })
      );
    },
  });

  useEffect(() => {
    if (!modalIsVisible) {
      formik.resetForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalIsVisible]);

  const handleClose = () => {
    if (modal.isLoading) return;
    formik.resetForm();
    dispatch(resetCreateOrganizationModal());
  };

  return (
    <Modal
      open={modalIsVisible}
      onClose={handleClose}
      aria-labelledby="create-organization-title"
    >
      <Box
        className="modal-box"
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          bgcolor: "#232323",
          borderRadius: "8px",
          boxShadow: 24,
          width: "440px",
          color: "white",
          p: 4,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: "1px solid #363636",
            padding: "5px 10px",
            marginBottom: "16px",
          }}
        >
          <Typography id="create-organization-title" variant="h6" component="h2">
            {t("organization.modals.addOrganization.title")}
          </Typography>
          <IconButton
            onClick={handleClose}
            sx={{ color: "white" }}
            disabled={modal.isLoading}
          >
            <CloseIcon />
          </IconButton>
        </div>

        <form onSubmit={formik.handleSubmit}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {t("organization.modals.addOrganization.name")}
          </Typography>
          <TextField
            fullWidth
            variant="outlined"
            name="orgName"
            value={formik.values.orgName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.orgName && Boolean(formik.errors.orgName)}
            helperText={formik.touched.orgName && formik.errors.orgName}
            sx={{
              "& .MuiInputBase-input": { color: "white" },
              "& .MuiOutlinedInput-root": {
                "& fieldset": { borderColor: "#4C4C4C" },
              },
              mb: 2,
            }}
          />

          <Typography variant="body2" sx={{ mb: 1 }}>
            {t("organization.modals.addOrganization.appId")}
          </Typography>
          <FormControl fullWidth variant="outlined" sx={{ mb: 2 }}>
            <Select
              name="appId"
              displayEmpty
              value={formik.values.appId}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.appId && Boolean(formik.errors.appId)}
              sx={{
                color: "white",
                ".MuiOutlinedInput-notchedOutline": {
                  borderColor: "#4C4C4C",
                },
                ".MuiSelect-select": {
                  padding: "10px",
                },
              }}
            >
              <MenuItem value="" disabled>
                {t("organization.modals.addOrganization.appId")}
              </MenuItem>
              {apps.map((app: IApp) => (
                <MenuItem
                  key={app.appId}
                  value={app.appId}
                  sx={{
                    color: "white",
                    backgroundColor: "#3b3b3b",
                    "&:hover": {
                      backgroundColor: "#414141",
                    },
                  }}
                >
                  {`${app.appName} (${app.appId})`}
                </MenuItem>
              ))}
            </Select>
            {formik.touched.appId && formik.errors.appId && (
              <Typography variant="caption" color="error">
                {formik.errors.appId}
              </Typography>
            )}
          </FormControl>

          <Typography variant="body2" sx={{ mb: 1 }}>
            {t("organization.modals.addOrganization.matrixServerIdentifier")}
          </Typography>
          <FormControl fullWidth variant="outlined" sx={{ mb: 2 }}>
            <Select
              name="matrixServerIdentifier"
              displayEmpty
              value={formik.values.matrixServerIdentifier}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.matrixServerIdentifier &&
                Boolean(formik.errors.matrixServerIdentifier)
              }
              sx={{
                color: "white",
                ".MuiOutlinedInput-notchedOutline": {
                  borderColor: "#4C4C4C",
                },
                ".MuiSelect-select": {
                  padding: "10px",
                },
              }}
            >
              <MenuItem value="" disabled>
                {t("organization.modals.addOrganization.matrixServerIdentifier")}
              </MenuItem>
              {uniqueMatrixServers.map((server) => (
                <MenuItem
                  key={server.identifier}
                  value={server.identifier}
                  sx={{
                    color: "white",
                    backgroundColor: "#3b3b3b",
                    "&:hover": {
                      backgroundColor: "#414141",
                    },
                  }}
                >
                  {`${server.type} - ${server.externalUrl}${
                    server.description ? ` - ${server.description}` : ""
                  }`}
                </MenuItem>
              ))}
            </Select>
            {formik.touched.matrixServerIdentifier &&
              formik.errors.matrixServerIdentifier && (
                <Typography variant="caption" color="error">
                  {formik.errors.matrixServerIdentifier}
                </Typography>
              )}
          </FormControl>

          <FormControlLabel
            control={
              <Checkbox
                checked={formik.values.addAdmin}
                onChange={(event) =>
                  formik.setFieldValue("addAdmin", event.target.checked)
                }
                sx={{ color: "white", "&.Mui-checked": { color: "#DBA94D" } }}
              />
            }
            label={t("organization.modals.addOrganization.addAdmin")}
            sx={{ mb: formik.values.addAdmin ? 1 : 2 }}
          />

          {formik.values.addAdmin && (
            <>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {t("organization.modals.addOrganization.initialAdmin")}
              </Typography>
              <TextField
                fullWidth
                variant="outlined"
                name="adminBpUserName"
                type="email"
                value={formik.values.adminBpUserName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.adminBpUserName &&
                  Boolean(formik.errors.adminBpUserName)
                }
                helperText={
                  formik.touched.adminBpUserName && formik.errors.adminBpUserName
                }
                sx={{
                  "& .MuiInputBase-input": { color: "white" },
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": { borderColor: "#4C4C4C" },
                  },
                  mb: 2,
                }}
              />
            </>
          )}

          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
            <Button
              type="submit"
              variant="contained"
              color="warning"
              disabled={modal.isLoading}
            >
              {t("organization.modals.addOrganization.submit")}
            </Button>
          </Box>
        </form>
      </Box>
    </Modal>
  );
};

export default CreateOrganizationModal;
